import {trimValue} from '../utilities/utilities';
import ScrollAnimation from './scroll-animation';
import HotSpot from './hotspot';
import PopupCard from './popup-card';

const settings = {
    classNames: {
        container: 'hotspot-illustration',
    },

    // Delay between hiding one card and showing another,
    // in milliseconds
    popupCardCrossnavDelay: 125,
};

export default class HotSpotIllustration extends ScrollAnimation {
    constructor(container) {
        // Call base class
        super(container);

        this.hotspotsVisible = false;

        // Tracks the currently active hotspot
        this.currentHotspot = null;

        // Find the legend
        this.legend = container.querySelector('.list--legend');

        // Find any hotspots in the section
        const hotspotElements = container
            .getElementsByClassName(HotSpot.classNames.hotspot);

        const hotspotCallbacks = {
            onSelect: this.hotspotSelected.bind(this),
            onDeselect: this.hotspotDeselected.bind(this),
        };

        this.hotspots = [];
        for (let i=0; i<hotspotElements.length; i++) {
            const hotspot = new HotSpot(hotspotElements[i], hotspotCallbacks);
            this.hotspots.push(hotspot);
        };

        // Set up event to close hotspots on negative space clicks
        document.body.addEventListener('click', (event) => {
            // Stop if there's no active hotspot
            if (!this.currentHotspot) {
                return;
            }

            this.handleBodyClickEvent(event);
        });

        // Sort hotspots by their vertical position - let's assume this
        // is the same order on both mobile & desktop
        this.sortedHotspots = [];
        for (let i=0; i<hotspotElements.length; i++) {
            this.sortedHotspots.push(hotspotElements[i]);
        }

        this.sortedHotspots.sort((a, b) => {
            return a.offsetTop - b.offsetTop;
        });

        // Setup popup cards for any hotspots that were located in the component
        this.popupCards = {};
        this.hotspots.forEach((hotspot) => {
            if (!hotspot.card) {
                return;
            }

            const popupCard = new PopupCard(
                hotspot.card,
                {
                    onClose: (keyboardEvent) => {
                        hotspot.deselect();

                        // If we closed due to a keyboard event, set focus
                        // on the hotspot
                        if (keyboardEvent) {
                            hotspot.button.focus();
                        }
                    },
                });

            this.popupCards[hotspot.cardName] = popupCard;

            // // Testing
            // if (hotspot.cardName == 'apcs') {
            //     hotspot.button.click();
            // }
        });
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new HotSpotIllustration(elements[i]);
        }
    }

    /**
     * Signals that we should update our position within the animation
     * @param {number} overallProgress
     * @param {number} topProgress
     * @param {number} centerProgress
     */
    setAnimationProgress(
        overallProgress,
        topProgress,
        centerProgress,
    ) {
        // Call base class
        super.setAnimationProgress(
            overallProgress,
            topProgress,
            centerProgress,
        );

        // Check if we've reached the scroll threshold where hotspots
        // should be visible
        if (topProgress >= 0.333) {
            // Hotspots should be visible
            this.showHotspots();
        } else {
            // Hotspots should be hidden
            this.hideHotspots();
        }
    }

    showHotspots() {
        if (this.hotspotsVisible) {
            return;
        }

        this.hotspotsVisible = true;

        // Stop any ongoing animation
        if (this.hotspotTimeline) {
            this.hotspotTimeline.progress(1);
        }

        this.hotspotTimeline = gsap.timeline();

        this.sortedHotspots.forEach((element, index) => {
            const progress = {
                percentComplete: 0,
            };

            this.hotspotTimeline.to(
                progress,
                1,
                {
                    ease: 'power2.out',
                    percentComplete: 1,
                    onUpdate: () => {
                        element.style.setProperty(
                            '--hotspot-illustration-offset',
                            trimValue(progress.percentComplete),
                        );
                    },
                },
                index * 0.0625,
            );
        });

        this.sortedHotspots.forEach((element, index) => {
            this.hotspotTimeline.to(
                element,
                1,
                {
                    ease: 'power1.inOut',
                    opacity: 1,
                    pointerEvents: 'all',
                },
                index * 0.0625,
            );
        });

        this.hotspotTimeline.to(
            this.legend,
            2,
            {
                ease: 'power2.out',
                opacity: 1,
            },
            '-=1',
        );

        this.hotspotTimeline.add(
            function() {
                this.hotspotTimeline = null;
            }.bind(this),
        );
    }

    hideHotspots() {
        if (!this.hotspotsVisible) {
            return;
        }

        this.hotspotsVisible = false;

        // Stop any ongoing animation
        if (this.hotspotTimeline) {
            this.hotspotTimeline.progress(1);
        }

        this.hotspotTimeline = gsap.timeline();

        this.sortedHotspots.forEach((element, index) => {
            this.hotspotTimeline.to(
                element,
                0.25,
                {
                    ease: 'power2.out',
                    opacity: 0,
                    pointerEvents: 'none',
                },
                0,
            );
        });

        this.hotspotTimeline.to(
            this.legend,
            0.25,
            {
                ease: 'power2.out',
                opacity: 0,
            },
            0,
        );

        this.hotspotTimeline.add(
            function() {
                this.hotspotTimeline = null;
            }.bind(this),
        );

        // Deselect hotspots
        this.deselectHotspots();
    }

    /**
     * Handles body click events to deselect hotspots - override in
     * derived classes to add additional logic, like stopping if an
     * element associated with a hotbox was clicked
     * @param {event} event
     */
    handleBodyClickEvent(event) {
        // Stop if the click occurred inside a popup card
        if (event.target.closest(`.${PopupCard.classNames.container}`)) {
            return;
        }

        // Deselect hotspots
        this.deselectHotspots();
    }

    /**
     * Called when a hotspot is selected - for overriding in derived
     * classes
     * @param {HotSpot} hotspot
     * @param {boolean} keyboardEvent
     */
    hotspotSelected(
        hotspot,
        keyboardEvent,
    ) {
        const hotspotWasSelected = this.currentHotspot !== null;

        this.deselectHotspots();
        this.currentHotspot = hotspot;

        // Show the associated popup card
        const popupCard = this.popupCards[hotspot.cardName];
        if (!popupCard) {
            return;
        }

        const callback = function() {
            // Clear the timeout variable
            this.newCardTimeout = null;

            // Show popup card
            popupCard.show(keyboardEvent === true);
        }.bind(this);

        if (hotspotWasSelected) {
            // A hotspot was just deselected - wait a moment
            // before showing the next popup card
            if (this.newCardTimeout) {
                clearTimeout(this.newCardTimeout);
            }

            this.newCardTimeout = setTimeout(
                callback,
                settings.popupCardCrossnavDelay,
            );
        } else {
            // A hotspot wasn't just deselected - show this card
            // right away
            callback();
        }
    }

    /**
     * Called when a hotspot is deselected - for overriding in derived
     * classes
     * @param {HotSpot} hotspot
     * @param {boolean} keyboardEvent
     */
    hotspotDeselected(
        hotspot,
        keyboardEvent,
    ) {
        if (this.currentHotspot == hotspot) {
            this.currentHotspot = null;
        }

        // Hide the associated popup card
        const popupCard = this.popupCards[hotspot.cardName];
        if (popupCard) {
            popupCard.hide();
        }
    }

    /**
     * Deselects any hotspots (currently only one may be selected
     * at a time)
     */
    deselectHotspots() {
        if (this.currentHotspot) {
            this.currentHotspot.deselect();
        }
    }
}
